import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import ReactHtmlParser from 'react-html-parser';
import './EventTabsSection.css';

const EventTabsSection = ({
  tabs,
  className = '',
  ...rest
}) => {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState(tabs[0]?.id || '');
  const [activeAccordion, setActiveAccordion] = useState(tabs[0]?.id || '');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const [accordionExpanded, setAccordionExpanded] = useState({});
  const [accordionShowReadMore, setAccordionShowReadMore] = useState({});
  const contentRef = useRef(null);
  const contentWrapperRef = useRef(null);
  const accordionContentRefs = useRef({});
  const accordionWrapperRefs = useRef({});
  
  // If activeTab no longer exists in the current tabs list (e.g. machine-test filtering
  // removes tabs after eventInfo loads), fall back to the first available tab so the
  // panel never renders blank.
  const resolvedActiveTab = tabs.some(t => t.id === activeTab) ? activeTab : (tabs[0]?.id || '');
  const activeTabData = tabs.find(tab => tab.id === resolvedActiveTab);

  const toggleAccordion = (tabId) => {
    // If clicking on the already active accordion, close it; otherwise, open the new one
    const newActiveAccordion = activeAccordion === tabId ? '' : tabId;
    setActiveAccordion(newActiveAccordion);
    
    // Reset expanded state when closing accordion
    if (newActiveAccordion === '') {
      setAccordionExpanded(prev => ({ ...prev, [tabId]: false }));
    }
  };

  // Check if content needs Read More button
  useEffect(() => {
    // Use setTimeout to ensure content is rendered before checking height
    const timer = setTimeout(() => {
      if (contentRef.current && contentWrapperRef.current) {
        const contentHeight = contentRef.current.scrollHeight;
        const maxHeight = 400; // Maximum height before showing Read More
        
        if (contentHeight > maxHeight) {
          setShowReadMore(true);
          setIsExpanded(false);
        } else {
          setShowReadMore(false);
          setIsExpanded(true);
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [activeTab, activeTabData]);

  // Reset expanded state when tab changes
  useEffect(() => {
    setIsExpanded(false);
  }, [activeTab]);

  // Handle topic link clicks for navigation
  useEffect(() => {
    const handleTopicLinkClick = (e) => {
      const topicLink = e.target.closest('.topic-link');
      if (topicLink) {
        e.preventDefault();
        const topicName = topicLink.getAttribute('data-topic-name');
        const href = topicLink.getAttribute('href');
        
        if (href && topicName) {
          // Navigate with topic in location state
          history.push({
            pathname: href,
            state: {
              selectedTopic: topicName
            }
          });
        }
      }
    };

    // Add click listener to document
    document.addEventListener('click', handleTopicLinkClick);
    
    return () => {
      document.removeEventListener('click', handleTopicLinkClick);
    };
  }, [history]);

  const toggleReadMore = () => {
    setIsExpanded(!isExpanded);
  };

  const toggleAccordionReadMore = (tabId) => {
    setAccordionExpanded(prev => ({
      ...prev,
      [tabId]: !prev[tabId]
    }));
  };

  // Check if accordion content needs Read More button
  useEffect(() => {
    if (activeAccordion) {
      const timer = setTimeout(() => {
        const contentRef = accordionContentRefs.current[activeAccordion];
        if (contentRef) {
          const contentHeight = contentRef.scrollHeight;
          const maxHeight = 300; // Maximum height for mobile accordion
          
          if (contentHeight > maxHeight) {
            setAccordionShowReadMore(prev => ({
              ...prev,
              [activeAccordion]: true
            }));
            // Only set to false if not already expanded
            setAccordionExpanded(prev => ({
              ...prev,
              [activeAccordion]: prev[activeAccordion] !== undefined ? prev[activeAccordion] : false
            }));
          } else {
            setAccordionShowReadMore(prev => ({
              ...prev,
              [activeAccordion]: false
            }));
            setAccordionExpanded(prev => ({
              ...prev,
              [activeAccordion]: true
            }));
          }
        }
      }, 200);

      return () => clearTimeout(timer);
    } else {
      // Reset expanded state when accordion closes
      setAccordionExpanded({});
      setAccordionShowReadMore({});
    }
  }, [activeAccordion]);

  return (
    <section className={`event-tabs-section ${className}`.trim()} {...rest}>
      {/* Tab Navigation for Desktop */}
      <div className="event-tabs-section__nav">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`event-tabs-section__tab ${resolvedActiveTab === tab.id ? 'event-tabs-section__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Tab Content for Desktop */}
      <div className="event-tabs-section__content">
        <div 
          ref={contentWrapperRef}
          className={`event-tabs-section__content-wrapper ${isExpanded ? 'event-tabs-section__content-wrapper--expanded' : ''}`}
        >
          <div ref={contentRef} className="event-tabs-section__content-inner">
            {activeTabData?.content ? (
              <div dangerouslySetInnerHTML={{ __html: activeTabData.content }} />
            ) : (
              <p>Content will be available soon.</p>
            )}
          </div>
        </div>
        {showReadMore && (
          <button
            type="button"
            className="event-tabs-section__read-more"
            onClick={toggleReadMore}
          >
            {isExpanded ? 'Read Less' : 'Read More'}
          </button>
        )}
      </div>

      {/* Accordion for Mobile */}
      <div className="event-tabs-section__accordion">
        {tabs.map((tab) => (
          <div 
            key={tab.id} 
            className={`event-tabs-section__accordion-item ${activeAccordion === tab.id ? 'event-tabs-section__accordion-item--active' : ''}`}
          >
            <button
              type="button"
              className="event-tabs-section__accordion-header"
              onClick={() => toggleAccordion(tab.id)}
              aria-expanded={activeAccordion === tab.id}
            >
              <span>{tab.label}</span>
              <svg 
                className="event-tabs-section__accordion-icon"
                width="16" 
                height="16" 
                viewBox="0 0 16 16" 
                fill="none"
              >
                <path 
                  d="M4 6L8 10L12 6" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <div className="event-tabs-section__accordion-content">
              <div className="event-tabs-section__accordion-content-inner">
                <div 
                  ref={el => accordionWrapperRefs.current[tab.id] = el}
                  className={`event-tabs-section__accordion-content-wrapper ${accordionExpanded[tab.id] ? 'event-tabs-section__accordion-content-wrapper--expanded' : ''}`}
                >
                  <div 
                    ref={el => accordionContentRefs.current[tab.id] = el}
                    className="event-tabs-section__accordion-content-text"
                  >
                    {tab.content ? (
                      <div dangerouslySetInnerHTML={{ __html: tab.content }} />
                    ) : (
                      <p>Content will be available soon.</p>
                    )}
                  </div>
                </div>
                {accordionShowReadMore[tab.id] && activeAccordion === tab.id && (
                  <button
                    type="button"
                    className="event-tabs-section__accordion-read-more"
                    onClick={() => toggleAccordionReadMore(tab.id)}
                  >
                    {accordionExpanded[tab.id] ? 'Read Less' : 'Read More'}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

EventTabsSection.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      content: PropTypes.string,
    })
  ).isRequired,
  className: PropTypes.string,
};

EventTabsSection.defaultProps = {
  className: '',
};

export default EventTabsSection;

